import React, { useState, useEffect } from 'react';
import { LiveKitRoom, RoomAudioRenderer, useVoiceAssistant } from '@livekit/components-react';
import '@livekit/components-styles';
import { Box, Button, Typography, CircularProgress, Alert } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LIVEKIT_TOKEN_ENDPOINT } from '../env';

function useThinkingSound(state) {
  useEffect(() => {
    if (state !== 'thinking') return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    let stopped = false;

    const playBeep = () => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
      osc.onended = () => { if (!stopped) setTimeout(playBeep, 800); };
    };

    playBeep();
    return () => { stopped = true; ctx.close(); };
  }, [state]);
}

function VoiceAssistantControls() {
  const { state, audioTrack } = useVoiceAssistant();
  useThinkingSound(state);

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Voice Assistant
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {state === 'listening' && '🎤 Listening...'}
        {state === 'thinking' && '🤔 Thinking...'}
        {state === 'speaking' && '🔊 Speaking...'}
        {state === 'idle' && '💤 Ready'}
      </Typography>
      {audioTrack && (
        <Box sx={{ 
          width: '100%', 
          height: 60, 
          backgroundColor: 'rgba(0,0,0,0.05)', 
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="caption">Audio Active</Typography>
        </Box>
      )}
    </Box>
  );
}

function VoiceChat({ userEmail }) {
  const [token, setToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);

  const fetchLiveKitToken = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Request microphone permission first (especially important for mobile)
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (permErr) {
        throw new Error('Microphone permission denied. Please allow microphone access and try again.');
      }

      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      
      if (!accessToken) {
        throw new Error('No authentication token available');
      }

      // Debug: Decode and log token payload
      try {
        const tokenParts = accessToken.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Token payload:', {
          client_id: payload.client_id,
          sub: payload.sub,
          exp: payload.exp,
          iat: payload.iat,
          token_use: payload.token_use
        });
        console.log('Token expires at:', new Date(payload.exp * 1000).toISOString());
      } catch (e) {
        console.error('Failed to decode token:', e);
      }

      console.log('Fetching LiveKit token from:', LIVEKIT_TOKEN_ENDPOINT);
      console.log('User email:', userEmail);
      
      // Generate unique room name to avoid state conflicts on reconnect
      const uniqueRoomName = `voice-chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await fetch(LIVEKIT_TOKEN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          roomName: uniqueRoomName,
          bearerToken: accessToken,
          userEmail: userEmail
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`Failed to get LiveKit token: ${response.status} - ${errorText}`);
        }
        throw new Error(errorData.error || errorData.message || 'Failed to get LiveKit token');
      }

      const data = await response.json();
      setToken(data.token);
      setLivekitUrl(data.url);
      setConnected(true);
    } catch (err) {
      console.error('Error fetching LiveKit token:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const disconnect = () => {
    setToken(null);
    setLivekitUrl(null);
    setConnected(false);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchLiveKitToken}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (!connected) {
    return (
      <Box sx={{ 
        p: 3, 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <MicIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.5 }} />
        <Typography variant="h5" gutterBottom>
          Voice Chat
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Talk to the Estonian concrete sales assistant using your voice
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <MicIcon />}
          onClick={fetchLiveKitToken}
          disabled={loading}
        >
          {loading ? 'Connecting...' : 'Start Voice Chat'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <LiveKitRoom
        token={token}
        serverUrl={livekitUrl}
        connect={true}
        audio={true}
        video={false}
        onDisconnected={disconnect}
      >
        <VoiceAssistantControls />
        <RoomAudioRenderer />
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<MicOffIcon />}
            onClick={disconnect}
          >
            End Voice Chat
          </Button>
        </Box>
      </LiveKitRoom>
    </Box>
  );
}

export default VoiceChat;
